import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ExternalLink, ArrowLeft, ChevronRight, Phone, Globe, Camera, Video } from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { PHOTO_VIDEO_ENTRIES, photoVideoSlug, FEATURED_TASTEBUDS_ENTRY, FEATURED_TASTEBUDS_ENTRY_FLASHBRUSH } from './PhotoVideo';
import SocialLinks, { countSocialLinks } from './SocialLinks';

// The two "Featured by TastebudsByAnubhi" cards on the category page are
// hardcoded entries (not part of the managed photo_video collection), so they
// are merged into the lookup pool here to give each one its own detail page.
const FEATURED_EXTRAS: any[] = [FEATURED_TASTEBUDS_ENTRY, FEATURED_TASTEBUDS_ENTRY_FLASHBRUSH];

const BASE_URL = 'https://dallasdesiscoop.com';

// Owner-supplied About copy for specific listings. When a slug is listed here,
// this text replaces the generic boilerplate paragraph on that detail page.
const ABOUT_OVERRIDES: Record<string, string> = {
  'a-dfwstills-photography':
    'We are professional photographers working in the DFW metroplex for over 20 years. And we provide beautiful and lively photos and videos to immortalize your celebrations. We also do live streaming and provide photo-booths.',
  'what-a-story':
    'What A Story specializes in Indian wedding photography and cinematography in Dallas, preserving the beauty, traditions, emotions, and unforgettable moments of every celebration. With 10+ years of experience and 1000+ events covered across the USA and India, we create timeless photographs and cinematic wedding films that tell your unique love story. Our services include pre-wedding photography, engagement sessions, Mehndi, Haldi, Sangeet, Baraat, traditional wedding ceremonies, candid photography, bridal and groom portraits, couple photoshoots, family portraits, reception coverage, cinematic wedding films, and multi-day wedding photography and videography. We cover Hindu weddings, South Indian weddings, North Indian weddings, multicultural celebrations, and grand destination weddings. Our approach focuses on authentic emotions, cultural traditions, family connections, intricate wedding details, and natural moments that make every celebration special. We serve couples across Dallas and the surrounding Dallas–Fort Worth area, including weddings at popular venues such as Hilton Anatole, Dallas Palms in Carrollton, The Room on Main, The Filter Building, and other wedding venues throughout the region. We also help capture beautiful wedding memories at scenic locations such as the Dallas Arboretum and Botanical Garden and White Rock Lake, subject to location permissions. Beyond Dallas, What A Story serves Los Angeles, San Jose, New York, and New Jersey, with photography and cinematography services available for wedding celebrations across the USA. Whether you are planning an intimate ceremony or a multi-day Indian wedding, our team is committed to documenting your traditions, emotions, and celebrations through beautiful photographs and cinematic storytelling tailored to your style.',
};

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=1200&h=800&fit=crop';

export default function PhotoVideoDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('photo_video', PHOTO_VIDEO_ENTRIES, (e, i) =>
    slugify(e.id || e.name || String(i))
  );

  const entry = mc.items.find((e: any) => photoVideoSlug(e) === slug) ?? FEATURED_EXTRAS.find((e) => photoVideoSlug(e) === slug);

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Photographer Not Found | Dallas Desi Scoop"
          description="This photo/video listing could not be found."
          canonical="/photo-video"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Listing not found.</p>
        <Link
          to="/photo-video"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Photo &amp; Video
        </Link>
      </div>
    );
  }

  // Instagram is surfaced only via the dedicated "Follow on Instagram" button in
  // the sidebar — strip it from the social-links row so no Instagram icon shows.
  const { instagram, instagramUrl, instagram_url, ...socialItem } = entry as any;
  const hasAnySocial = countSocialLinks(socialItem) > 0;
  const canonicalPath = `/photo-video/${slug}`;

  const title = `${entry.name} | Desi Wedding Photographer & Videographer in Dallas, DFW | Dallas Desi Scoop`;
  const metaDescription = `${entry.name} is a Desi wedding photographer and videographer serving Dallas-Fort Worth.${entry.description ? ` ${entry.description}` : ''} View portfolio, services, and contact details on Dallas Desi Scoop.`;

  const jsonLd = [
    {
      '@type': 'ProfessionalService',
      '@id': `${BASE_URL}${canonicalPath}#business`,
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description:
        entry.description ||
        `${entry.name}, Desi wedding photographer and videographer serving the Dallas-Fort Worth metroplex.`,
      url: entry.website || entry.instagramUrl || entry.instagram || `${BASE_URL}${canonicalPath}`,
      areaServed: {
        '@type': 'City',
        name: 'Dallas-Fort Worth',
      },
      address: {
        '@type': 'PostalAddress',
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      telephone: entry.phone || undefined,
      sameAs: [
        entry.instagramUrl || entry.instagram,
        entry.facebook,
        entry.youtube,
        entry.tiktok,
        entry.website,
      ].filter(Boolean),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Photo & Video', item: `${BASE_URL}/photo-video` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other photo/video professionals on DallasDesiScoop.
  const relatedEntries = mc.items
    .filter((e: any) => photoVideoSlug(e) !== slug)
    .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)))
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/photo-video"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Photo &amp; Video
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <Camera className="w-3.5 h-3.5 text-[#51ba4f]" />
              Photography
            </span>
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <Video className="w-3.5 h-3.5 text-[#51ba4f]" />
              Videography
            </span>
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
              Serving Dallas–Fort Worth, TX
            </span>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          {ABOUT_OVERRIDES[String(slug)] ? (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
              {ABOUT_OVERRIDES[String(slug)]}
            </p>
          ) : (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
              {entry.name} is a Desi wedding photographer and videographer serving the Dallas–Fort Worth metroplex.
              {entry.description ? ` ${entry.description}` : ''}
              {` From Indian and Pakistani weddings, engagements, bridal shoots, mehndi, and sangeet to receptions, birthdays, family portraits, and cultural events, ${entry.name} covers celebrations across Dallas, Frisco, Plano, Irving, McKinney, Carrollton, Richardson, and the wider DFW area. Browse ${entry.name} and more photo and video professionals on Dallas Desi Scoop.`}
            </p>
          )}

          {hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={socialItem} px={20} />
            </div>
          )}

        </div>

        {/* Contact & Info */}
        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact &amp; Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
              <span>{entry.address || 'Dallas–Fort Worth, TX'}</span>
            </div>
            {entry.phone && (
              <a
                href={`tel:${String(entry.phone).replace(/[^\d+]/g, '')}`}
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{entry.phone}</span>
              </a>
            )}
            {(entry.instagramUrl || entry.instagram) && (
              <a
                href={(entry.instagramUrl || entry.instagram) as string}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full bg-gradient-to-r from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] hover:opacity-90 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-opacity"
              >
                <span>Follow on Instagram</span>
              </a>
            )}
            {entry.website && (
              <a
                href={entry.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>Visit website</span>
              </a>
            )}
          </div>
          <Link
            to="/photo-video"
            className="mt-3 flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            All Photo &amp; Video in DFW
          </Link>
        </div>
      </div>

      {/* Internal links: related photo/video professionals */}
      {relatedEntries.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 pb-8">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">More Desi Photographers &amp; Videographers in DFW</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {relatedEntries.map((e: any) => (
              <Link
                key={photoVideoSlug(e)}
                to={`/photo-video/${photoVideoSlug(e)}`}
                className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-4 py-3 text-[13px] font-medium text-gray-700 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
              >
                <Camera className="w-3.5 h-3.5 text-[#f97316] shrink-0" />
                {e.name}
                <span className="ml-auto text-gray-400 text-[11px]">DFW</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Internal links: explore DallasDesiScoop */}
      <div className="max-w-[1000px] mx-auto px-4 pb-12">
        <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/photo-video', label: 'All Photo & Video in DFW' },
            { to: '/restaurants', label: 'Desi Restaurants' },
            { to: '/events', label: 'Desi Events in DFW' },
            { to: '/shopping', label: 'Desi Shopping' },
            { to: '/blog', label: 'DFW Desi Guides' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="inline-flex items-center gap-1.5 bg-white rounded-full border border-gray-200 px-4 py-2 text-[12px] font-semibold text-gray-600 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}